import { getData, getStitchedBmusData } from "api/dataVisualisation/client";
import {
  DynamicDataModel,
  DynamicDataset,
} from "models/bmuData/dynamicDataModel";
import {
  DynamicParametersDataModel,
  DynamicParametersDataset,
} from "models/bmuData/dynamicParametersDataModel";
import {
  DynamicRatesDataModel,
  DynamicRatesDataset,
} from "models/bmuData/dynamicRatesDataModel";
import { DataDownloadFormat } from "models/dataDownload/dataDownloadFormat";
import {
  DataDownloadModel,
  JsonDataDownloadModel,
} from "models/dataDownload/dataDownloadModel";
import DateFilterModel from "models/filterModels/dateFilterModel";
import { getSettlementDateAndPeriodFromSettlementTime } from "utils/DateUtils";
import { toDateOnlyString } from "utils/dateHelpers";

export const getDynamicData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicDataset[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicDataModel>> => {
  const { data } = await getData("/balancing/dynamic", {
    snapshotAt: dateFilter.normalisedStartDate.toISOString(),
    until: dateFilter.normalisedEndDate.toISOString(),
    bmUnit,
    dataset: datasets,
    format,
  });
  return data;
};

export const getDynamicJsonData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicDataset[]
): Promise<JsonDataDownloadModel<DynamicDataModel>> =>
  getDynamicData(
    dateFilter,
    bmUnit,
    datasets,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicDataModel>>;

export const getDynamicRatesData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicRatesDataset[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicRatesDataModel>> => {
  const { data } = await getData("/balancing/dynamic/rates", {
    snapshotAt: dateFilter.normalisedStartDate.toISOString(),
    until: dateFilter.normalisedEndDate.toISOString(),
    bmUnit,
    dataset: datasets,
    format,
  });
  return data;
};

export const getDynamicRatesJsonData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicRatesDataset[]
): Promise<JsonDataDownloadModel<DynamicRatesDataModel>> =>
  getDynamicRatesData(
    dateFilter,
    bmUnit,
    datasets,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicRatesDataModel>>;

export const getDynamicParametersData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicParametersDataset[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicParametersDataModel>> => {
  const { data } = await getData("/balancing/dynamic/dynamicParameters", {
    from: dateFilter.normalisedStartDate.toISOString(),
    to: dateFilter.normalisedEndDate.toISOString(),
    bmUnit,
    dataset: datasets,
    format,
  });
  return data;
};

export const getDynamicParametersJsonData = async (
  dateFilter: DateFilterModel,
  bmUnit: string,
  datasets?: DynamicParametersDataset[]
): Promise<JsonDataDownloadModel<DynamicParametersDataModel>> =>
  getDynamicParametersData(
    dateFilter,
    bmUnit,
    datasets,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicParametersDataModel>>;

export const getMarketWideDynamicData = async (
  dataset: DynamicDataset,
  date: Date,
  bmUnits?: string[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicDataModel>> => {
  const { settlementDate, settlementPeriod } =
    getSettlementDateAndPeriodFromSettlementTime(date);
  return getStitchedBmusData(
    "/balancing/dynamic/all",
    {
      dataset: dataset,
      settlementDate: toDateOnlyString(settlementDate),
      settlementPeriod: settlementPeriod,
    },
    bmUnits,
    format
  );
};

export const getMarketWideDynamicRatesData = async (
  dataset: DynamicRatesDataset,
  date: Date,
  bmUnits?: string[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicRatesDataModel>> => {
  const { settlementDate, settlementPeriod } =
    getSettlementDateAndPeriodFromSettlementTime(date);
  return getStitchedBmusData(
    "/balancing/dynamic/rates/all",
    {
      dataset: dataset,
      settlementDate: toDateOnlyString(settlementDate),
      settlementPeriod: settlementPeriod,
    },
    bmUnits,
    format
  );
};

export const getMarketWideDynamicJsonData = async (
  dataset: DynamicDataset,
  settlementDate: Date,
  bmUnits?: string[]
): Promise<JsonDataDownloadModel<DynamicDataModel>> =>
  getMarketWideDynamicData(
    dataset,
    settlementDate,
    bmUnits,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicDataModel>>;

export const getMarketWideDynamicRatesJsonData = async (
  dataset: DynamicRatesDataset,
  settlementDate: Date,
  bmUnits?: string[]
): Promise<JsonDataDownloadModel<DynamicRatesDataModel>> =>
  getMarketWideDynamicRatesData(
    dataset,
    settlementDate,
    bmUnits,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicRatesDataModel>>;

export const getMarketWideDynamicParametersData = async (
  dataset: DynamicParametersDataset,
  date: Date,
  bmUnits?: string[],
  format?: DataDownloadFormat
): Promise<DataDownloadModel<DynamicParametersDataModel>> => {
  const { settlementDate, settlementPeriod } =
    getSettlementDateAndPeriodFromSettlementTime(date);
  return getStitchedBmusData(
    "/balancing/dynamic/dynamicParameters/all",
    {
      dataset: dataset,
      settlementDate: toDateOnlyString(settlementDate),
      settlementPeriod: settlementPeriod,
    },
    bmUnits,
    format
  );
};

export const getMarketWideDynamicParametersJsonData = async (
  dataset: DynamicParametersDataset,
  settlementDate: Date,
  bmUnits?: string[]
): Promise<JsonDataDownloadModel<DynamicParametersDataModel>> =>
  getMarketWideDynamicParametersData(
    dataset,
    settlementDate,
    bmUnits,
    DataDownloadFormat.Json
  ) as Promise<JsonDataDownloadModel<DynamicParametersDataModel>>;
